import React, { useState } from "react";
import { useGlobalWeather, useWeeklyWeatherTrends, useExtremeEvents } from "../hooks/useData";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const REGIONS = ["global", "europe", "asia", "americas", "africa"];

export default function Weather() {
  const [region, setRegion] = useState("global");
  const { data: globalSummary, isLoading } = useGlobalWeather();
  const { data: trends } = useWeeklyWeatherTrends(region === "global" ? undefined : region);
  const { data: extreme } = useExtremeEvents();

  const cities = globalSummary?.cities || [];

  // Build trend chart data from first city
  const trendData = trends?.trends?.[0]?.forecast?.slice(0, 20).map(f => ({
    time: f.dt_txt?.slice(5, 16),
    temp: f.temp_c,
    humidity: f.humidity,
  })) || [];

  return (
    <div className="fade-in">
      <h1 style={{ fontFamily: "var(--mono)", fontSize: 20, marginBottom: 6 }}>WEATHER</h1>
      <p style={{ color: "var(--subtext)", fontSize: 12, marginBottom: 20 }}>
        Global weather conditions via OpenWeatherMap
      </p>

      {/* City grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: 12, marginBottom: 24 }}>
        {isLoading ? Array(12).fill(0).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 90 }} />
        )) : cities.map((c, i) => (
          <div key={i} className="card" style={{ textAlign: "center", padding: "14px 10px" }}>
            <div style={{ fontSize: 22, fontFamily: "var(--mono)", fontWeight: 700, color: "var(--accent2)" }}>
              {c.temp_c?.toFixed(1)}°
            </div>
            <div style={{ fontWeight: 600, fontSize: 12, margin: "4px 0" }}>{c.city}</div>
            <div style={{ fontSize: 10, color: "var(--subtext)" }}>{c.description}</div>
            <div style={{ fontSize: 10, color: "var(--subtext)" }}>💧 {c.humidity_pct}%</div>
          </div>
        ))}
      </div>

      {/* Trend chart */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Weekly Temperature Trend</div>
          <div style={{ display: "flex", gap: 6 }}>
            {REGIONS.map(r => (
              <button key={r} onClick={() => setRegion(r)} style={{
                padding: "4px 10px", borderRadius: 4, cursor: "pointer",
                border: `1px solid ${r === region ? "var(--accent)" : "var(--border)"}`,
                background: r === region ? "rgba(0,229,176,0.1)" : "var(--surface2)",
                color: r === region ? "var(--accent)" : "var(--subtext)",
                fontSize: 10, fontFamily: "var(--mono)",
              }}>{r}</button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={trendData}>
            <XAxis dataKey="time" tick={{ fill: "var(--subtext)", fontSize: 9 }} axisLine={false} tickLine={false} interval={3} />
            <YAxis tick={{ fill: "var(--subtext)", fontSize: 9 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11 }} />
            <Legend wrapperStyle={{ fontSize: 11, color: "var(--subtext)" }} />
            <Line type="monotone" dataKey="temp" name="Temp °C" stroke="var(--accent2)" dot={false} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Extreme events */}
      {extreme?.events?.length > 0 && (
        <div className="card">
          <div className="card-title">Extreme Weather Events</div>
          {extreme.events.map((e, i) => (
            <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ color: "#FF4444", fontSize: 11, fontFamily: "var(--mono)", flexShrink: 0 }}>ALERT</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{e.event} · {e.city}</div>
                  <div style={{ fontSize: 11, color: "var(--subtext)", marginTop: 2 }}>{e.description}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
